import { useApp } from "@/context/AppContext";
import { DocHeader } from "@/components/DocHeader";
import { fmtDate, fmtNum } from "@/lib/format";
import { FileDown, ListOrdered, Printer } from "lucide-react";
import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import CashStatementDoc from "@/components/finance/CashStatementDoc";
import { elementToPdf } from "@/lib/pdf";
import { printElement } from "@/lib/print";
import { BigButton } from "@/components/ui-kit";
import { RatingBar } from "@/components/reports/Rating";
const vals = (o) => Object.values(o || {});

export function ReportActions({ target, name, printLabel, wide = false, brand = true }) {
  const { t } = useApp();
  return (
    <div className="grid grid-cols-2 gap-2 no-print">
      <BigButton variant="soft" onClick={() => elementToPdf(target.current, `${name}.pdf`, { receipt: !wide, brand })} data-testid="report-pdf-btn"><FileDown className="w-5 h-5" />PDF</BigButton>
      <BigButton onClick={() => printElement(target.current, { receipt: !wide, title: name, brand })} data-testid="report-print-btn"><Printer className="w-5 h-5" />{printLabel || t("print")}</BigButton>
    </div>
  );
}

export function XReportCard({ r, shift }) {
  const { t, view } = useApp();
  const ref = useRef(null);
  const [detail, setDetail] = useState(false);
  return (
    <div className="space-y-3">
      <div ref={ref} className="print-area font-mono-receipt text-sm p-4 bg-white rounded border border-border" data-testid="x-report">
        <DocHeader center title={t("x_report")} sub={<div>{shift?.opened_by}</div>} />
        <ReportLines r={r} />
      </div>
      <ReportActions target={ref} name={`arqueo-${new Date().toISOString().slice(0, 10)}`} />
      <BigButton variant="soft" onClick={() => setDetail(true)} className="w-full no-print" data-testid="x-detail-btn"><ListOrdered className="w-5 h-5" />{t("cash_statement")} (A4)</BigButton>
      {detail && <Dialog open onOpenChange={(o) => !o && setDetail(false)}><DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto" data-testid="cash-statement-dialog"><DialogTitle className="font-heading">{t("cash_statement")} · {t("x_report")}</DialogTitle><CashStatementDoc from={shift?.opened_at} to={new Date().toISOString()} opening={shift?.opening_float || 0} opener={shift?.opened_by} title={`${t("x_report")} · ${shift?.opened_by || ""}`} filters={[`${t("status")} = ${t("shift_open")}`]} /></DialogContent></Dialog>}
    </div>
  );
}

const ROWS = [
  ["sales_count", "count", true], ["gross_sales", "gross"], ["discount", "discounts"], ["net_sales", "net"],
  ["cash_sales", "cash_sales"], ["card_sales", "card_sales"], ["split_sales", "split_sales"], ["house_sales", "house_sales"], ["credit_sales", "credit_sales"], ["credit_paid_now", "credit_paid_now"], ["credit_given", "credit_given"],
  ["debt_payments", "debt_payments"], ["returns_total", "returns_total"], ["cash_refunds", "cash_refunds"], ["expenses", "expenses"],
  ["opening_float", "opening_float"], ["cash_in", "cash_in"], ["cash_out", "cash_out"], ["drawer_opens", "drawer_opens", true],
];

export function ReportLines({ r }) {
  const { t } = useApp();
  return (
    <div className="space-y-0.5" data-testid="shift-report-lines">
      {ROWS.map(([label, k, plain]) => (
        <div key={k} className="flex justify-between gap-2"><span>{t(label)}</span><span dir="ltr" data-testid={`shift-r-${k}`}>{plain ? r[k] : fmtNum(r[k])}</span></div>
      ))}
      <div className="flex justify-between font-bold border-t border-dashed border-foreground/40 pt-1 mt-1"><span>{t("expected_cash")}</span><span dir="ltr" data-testid="shift-r-expected">{fmtNum(r.expected)}</span></div>
    </div>
  );
}

export default function ZReport({ shift, actions = true }) {
  const { t, view, lang, money } = useApp();
  const ref = useRef(null);
  const [detail, setDetail] = useState(false);
  const r = shift.report || {};
  const diff = Math.round((shift.counted - shift.expected) * 100) / 100;
  return (
    <div className="space-y-3">
    {detail && <Dialog open onOpenChange={(o) => !o && setDetail(false)}><DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto" data-testid="cash-statement-dialog"><DialogTitle className="font-heading">{t("cash_statement")}</DialogTitle><CashStatementDoc from={shift.opened_at} to={shift.closed_at || new Date().toISOString()} opening={shift.opening_float || 0} opener={shift.opened_by} title={`${t("shift")} · ${shift.opened_by}`} /></DialogContent></Dialog>}
    <div ref={ref} className="print-area font-mono-receipt text-sm p-4 bg-white rounded-xl border border-border" data-testid="z-report">
      <DocHeader center title={t("z_report")} sub={<><div>{fmtDate(shift.opened_at, lang)} → {fmtDate(shift.closed_at, lang)}</div><div>{shift.opened_by} / {shift.closed_by}</div></>} />
      <ReportLines r={r} />
      <div className="flex justify-between"><span>{t("counted_cash")}</span><span dir="ltr">{fmtNum(shift.counted)}</span></div>
      <div className={`flex justify-between font-bold text-base ${diff === 0 ? "" : "text-destructive"}`}><span>{t("difference")}</span><span data-testid="z-report-diff">{money(diff)}</span></div>
      <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-foreground/30"><span>{t("charity")} · {t("charity_in_drawer")}</span><span dir="ltr" data-testid="z-report-charity">{money(r.charity_cash || 0)}</span></div>
      <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-foreground/30"><span>{t("satisfaction")}</span><RatingBar sales={vals(view.sales).filter((s) => s.rating && (s.rating_shift_id === shift.id || (!s.rating_shift_id && s.created_at >= shift.opened_at && (!shift.closed_at || s.created_at <= shift.closed_at))))} t={t} compact /></div>
    </div>
    {actions && <ReportActions target={ref} name={`z-report-${(shift.closed_at || "").slice(0, 10)}`} printLabel={t("print_z")} />}
    {actions && <BigButton variant="soft" onClick={() => setDetail(true)} className="w-full no-print" data-testid="z-detail-btn"><ListOrdered className="w-5 h-5" />{t("cash_statement")} (A4)</BigButton>}
    </div>
  );
}
